import {
  Alert, Box, CircularProgress, Container, Snackbar, Typography, useTheme, Link as MuiLink,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo, useRef, useState,
} from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { unwrapResult } from '@reduxjs/toolkit';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useAppDispatch } from '../../store/hooks';
import { useLogo } from '../../config/useLogo';
import {
  emailRegex,
  ImproveIt360SolutionOrgName,
} from '../../common/constants';
import { TestIds } from '../../assets/testIds';
import improveIt360Logo from '../../assets/images/improve_it_360_logo.svg';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import { Logger } from '../../config/logging/logger';
import OptimusButton from '../../common/library/Button/Button';
import OptimusInput from '../../common/library/Input/Input';
import { memberRegistration } from '../../store/thunks/authenticationThunk';
import { MemberRegistrationRequest } from '../../common/models/AuthenticationRequest';
import { useCustomEventTracking } from '../../common/useCustomEventTracking';
import RecaptchaV3 from '../../common/RecaptchaV3';
import RecaptchaV2 from '../../common/RecaptchaV2';
import { RecaptchaFlag } from '../../common/OptimizelyFlagsConstants';
import { useOptimizelyFlag } from '../../common/hooks/useOptimizelyFlag';
import { RecaptchaActions, RegistrationSource } from '../../common/enums';

type RegistrationSignUpProps = {
  email?: string;
};

export function RegistrationSignUp({ email }: RegistrationSignUpProps) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState(email || '');
  const [completed, setCompleted] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [recaptchaV2Token, setRecaptchaV2Token] = useState<string | undefined>(undefined);
  const [isV2TokenValid, setIsV2TokenValid] =
    useState<boolean | null>(null);
  const [isV3TokenValid, setIsV3TokenValid] =
    useState<boolean | null>(null);
  const latestEmailRef = useRef('');
  const autoSubmittedRef = useRef(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { secondaryOne: Logo } = useLogo();
  const solutionOrg = searchParams.get('solutionOrg');
  const logger = useMemo(() => Logger.create('SignUp'), []);
  const { trackSignUp } = useCustomEventTracking();

  const { solutionOrgName, termsOfServiceUrl, privacyPolicyUrl } = useSolutionOrg();
  const isImproveIt360 = solutionOrgName === ImproveIt360SolutionOrgName;

  const computeRegistrationLinkBaseUrl = useCallback((): string | undefined => {
    try {
      const baseUrl =
        process.env.REACT_APP_SERVICE_NATION_BY_EVER_PRO_BASE_URL || window.location.origin;
      const url = new URL(baseUrl);
      url.pathname = '/signup';
      url.search = '';
      if (solutionOrg) {
        url.searchParams.set('solutionOrg', solutionOrg);
      }
      return url.toString();
    } catch (err) {
      logger.warn(Logger.format('Failed to build registration link base url', err));
      return undefined;
    }
  }, [logger, solutionOrg]);

  const validate = (raw: string) => {
    const candidate = raw.trim().toLowerCase(); // normalize here
    if (!candidate) return 'Email is required';
    emailRegex.lastIndex = 0;
    const isValid = emailRegex.test(candidate);
    if (!isValid) return 'Invalid email format';
    return null;
  };

  // get component based on optimizely decision
  const {
    decision: recaptchaDecision,
    isDecisionReady: isRecaptchaDecisionReady,
  } = useOptimizelyFlag({
    flag: RecaptchaFlag,
  });

  const isCTADisabled = isRegistering ||
    // disable the login button if the user fails the invisible based reCAPTCHA
    // and has not completed the visible reCAPTCHA yet
    (isV3TokenValid === false && !recaptchaV2Token) ||
    // disable the login button if the user fails the challenge based reCAPTCHA
    (isV2TokenValid === false);

  const handleSubmit = async (
    e?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ) => {
    // Support both form submit and button click
    if (e && 'preventDefault' in e) e.preventDefault();

    try {
      const raw = latestEmailRef.current || registrationEmail;
      const validationError = validate(raw);
      setErrorMsg(validationError);

      if (!validationError) {
        setIsRegistering(true);
        const cleanedEmail = raw.trim().toLowerCase();
        const registrationLinkBaseUrl = computeRegistrationLinkBaseUrl();

        let recaptchaV3Token: string | undefined;
        if (isRecaptchaDecisionReady && recaptchaDecision.enabled) {
          // get invisible reCAPTCHA token
          if (isV3TokenValid === null) {
            recaptchaV3Token = await new Promise((resolve) => {
              window.grecaptcha?.enterprise.ready(async () => {
                const token = await window.grecaptcha?.enterprise.execute(
                  process.env.REACT_APP_GOOGLE_RECAPTCHA_V3_SITE_KEY || '',
                  { action: RecaptchaActions.Register },
                );
                resolve(token as string);
              });
            });
          }
        }

        const registrationRequest: MemberRegistrationRequest = {
          email: cleanedEmail,
          recaptchaV2Token,
          recaptchaV3Token,
          source: RegistrationSource.EDGE,
          ...(registrationLinkBaseUrl ? { registrationLinkBaseUrl } : {}),
        } as MemberRegistrationRequest;
        dispatch(memberRegistration(registrationRequest))
          .then(unwrapResult)
          .then((registrationResponse) => {
            setOpenErrorSnackbar(false);
            setIsRegistering(false);
            if (isRecaptchaDecisionReady && recaptchaDecision.enabled) {
              // if the user fails the invisible reCAPTCHA, we do not register him
              if (registrationResponse?.isV3TokenValid === false) {
                setIsV3TokenValid(false);
                return;
              }
              // if the user fails the challenge based reCAPTCHA, we do not let him register
              // the user has failed both reCAPTCHAs and is likely a bot at this point
              if (registrationResponse?.isV2TokenValid === false) {
                setIsV2TokenValid(false);
                return;
              }
            }
            setCompleted(true);
            trackSignUp('Auth', 'Registration', 'Member', { email: cleanedEmail });
          })
          .catch((err: unknown) => {
            setOpenErrorSnackbar(true);
            setIsRegistering(false);
            logger.error(Logger.format(err));
          });
      }
    } catch (err) {
      setOpenErrorSnackbar(true);
      logger.error(Logger.format(err));
    }
  };

  useEffect(() => {
    if (!email) return;
    if (autoSubmittedRef.current) return;
    if (completed) return;
    setIsRegistering(true);
    const interval = setInterval(() => {
      // loop until recaptcha is loaded and the feature flag is ready
      if (!window.grecaptcha) return;
      if (!isRecaptchaDecisionReady) return;
      clearInterval(interval);

      autoSubmittedRef.current = true;
      // Set both state and the ref so handleSubmit sees the value immediately
      setRegistrationEmail(email);
      latestEmailRef.current = email;
      handleSubmit();

      // eslint-disable-next-line consistent-return
      return () => clearInterval(interval);
    }, 200);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, isRecaptchaDecisionReady]);

  const handleClose = () => {
    setOpenErrorSnackbar(false);
  };

  const clearError = () => {
    if (errorMsg) {
      setErrorMsg(null);
    }
  };

  const openLoginPage = () => {
    trackSignUp('Auth', 'Login', 'Member');
    navigate('/login?afterLoginPath=/home');
  };

  const resetForm = () => {
    setCompleted(false);
    setErrorMsg(null);
    setRegistrationEmail('');
  };

  return (
    <Container
      maxWidth='sm'
      sx={{
        background: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100vh',
        pr: theme.spacing(3),
        pl: theme.spacing(3),
      }}
    >
      <>
        <Snackbar
          open={openErrorSnackbar}
          autoHideDuration={3000}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {t('error.generalMessage')}
          </Alert>
        </Snackbar>
        <Box sx={{ textAlign: 'left' }}>
          {Logo && <img src={Logo as string} alt="Logo" style={{ width: theme.spacing(20.625) }} />}
          {isImproveIt360 && (
            <Box
              sx={{
                display: 'flex', alignItems: 'center', mt: 1, mb: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{ mr: 1, color: theme.palette.grey[600] }}
              >
                {t('login.broughtToYouBy')}
              </Typography>
              <img
                src={improveIt360Logo}
                alt="Improve It 360 Logo"
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            marginTop: theme.spacing(4),
          }}
        >
          <Typography
            sx={{
              textAlign: 'left',
              mb: theme.spacing(1),
            }}
            variant='h4'>{t('signUp.heading')}
          </Typography>
          {completed ?
            (<Box
              sx={{ marginTop: theme.spacing(3) }}
            >
              <Typography
                component="p"
                variant='bodyLg'
                sx={{
                  color: theme.palette.grey[600],
                  textAlign: 'left',
                }}

              >
                <Trans
                  i18nKey="signUp.registrationMagicLinkSent"
                  values={{ email: registrationEmail }}
                  components={{
                    boldEmail: <strong />,
                    break: <br />,
                  }}
                />
              </Typography>
              <Typography
                component="p"
                variant='bodyMd'
                sx={{
                  color: theme.palette.grey[600],
                  textAlign: 'left',
                  mt: theme.spacing(2),
                }}
              >
                <Trans
                  i18nKey="signUp.registrationMagicLinkNotSent"
                  components={{
                    contact: (
                      <a
                        href="/contact-support"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {/* Fallback text, will be replaced by translation,
                            only used to fix eslint issue */}
                            contact support
                      </a>
                    ),
                  }}
                />
              </Typography>
              <OptimusButton
                data-testid={TestIds.signup.editEmailLnk}
                size={'m'}
                customVariant='secondaryGray'
                fullWidth
                sx={{
                  mb: theme.spacing(2),
                  mt: theme.spacing(3),
                  alignSelf: 'center',
                }}
                disabled={isRegistering}
                type="button"
                onClick={resetForm}
                buttonText={t('signUp.editEmail') || ''}
              />
            </Box>) :
            (<Box
              sx={{ marginTop: theme.spacing(3) }}
            >
              <Box component="form" onSubmit={handleSubmit}>
                <OptimusInput
                  data-testid={TestIds.signup.emailInput}
                  size='m'
                  width={'100%'}
                  sx={{
                    mb: theme.spacing(3),
                  }}
                  customVariant='iconLeading'
                  helpIcon={false}
                  leadingIcon={<EmailOutlinedIcon />}
                  label='Email'
                  required={true}
                  placeholderText={t('signUp.emailPlaceholderText') as string}
                  value={registrationEmail}
                  onInputChange={(e) => {
                    const v = e.target.value;
                    if (errorMsg) clearError();
                    setRegistrationEmail(v);
                    latestEmailRef.current = v;
                  }}
                  error={!completed && !!errorMsg}
                  helperText={!completed ? errorMsg || undefined : undefined}
                />
                {isRecaptchaDecisionReady && recaptchaDecision.enabled && <>
                  {isV3TokenValid === null &&
                    <RecaptchaV3 />
                  }
                  {isV3TokenValid === false &&
                    <RecaptchaV2
                      onVerify={(token: string) => setRecaptchaV2Token(token)}
                      onExpired={() => setRecaptchaV2Token(undefined)}
                    />
                  }</>
                }
                <OptimusButton
                  data-testid={TestIds.signup.registrationBtn}
                  size={'m'}
                  fullWidth
                  sx={{
                    mb: theme.spacing(0),
                    alignSelf: 'center',
                  }}
                  disabled={isCTADisabled}
                  type="submit"
                  buttonText={isRegistering ?
                    <CircularProgress color="inherit" size={24} /> :
                    t('button.continue') || ''}
                />
                <OptimusButton
                  data-testid={TestIds.signup.existingAccountBtn}
                  size={'m'}
                  customVariant='secondaryGray'
                  fullWidth
                  sx={{
                    mb: theme.spacing(2),
                    mt: theme.spacing(3),
                    alignSelf: 'center',
                  }}
                  disabled={isCTADisabled}
                  type="button"
                  onClick={openLoginPage}
                  buttonText={t('button.existingAccount') || ''}
                />
              </Box>
            </Box>)
          }
        </Box>
        <Box
          sx={{
            mt: theme.spacing(3),
            textAlign: 'center',
          }}
        >
          <Typography variant='body7'>
            {t('signUp.agreement')}&nbsp;
            <MuiLink
              href={termsOfServiceUrl as string}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${t('home.tosHeading')} ${t('onboarding.common.opensNewTab')}`}
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {t('home.tosHeading')}
            </MuiLink>
            &nbsp;{t('onboarding.common.and')}&nbsp;
            <MuiLink
              href={privacyPolicyUrl as string}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${t('home.privacyPolicyHeading')} ${t('onboarding.common.opensNewTab')}`}
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {t('home.privacyPolicyHeading')}
            </MuiLink>.
          </Typography>
        </Box>
      </>
    </Container>
  );
}
